import React, { useState, useEffect, useRef } from 'react'
import { X, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'react-toastify'
import projectService from '../../services/projectService'
import './ProjectSelectionModal.css'

const ProjectSelectionModal = ({ isOpen, onClose, onSelectProject, selectedProjectCode = null }) => {
  const [projectData, setProjectData] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProject, setSelectedProject] = useState(null)
  const userHasSelectedRef = useRef(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const itemsPerPage = 10

  // Load project data
  useEffect(() => {
    if (isOpen) {
      // Reset to page 1 when search term changes
      if (searchTerm !== '') {
        setCurrentPage(1)
      }
      // Debounce search
      const timeoutId = setTimeout(() => {
        loadData()
      }, 300)
      
      return () => clearTimeout(timeoutId)
    }
  }, [isOpen, currentPage, searchTerm])

  // Reset selection only when modal first opens
  useEffect(() => {
    if (isOpen) {
      // Reset selection when modal opens
      setSelectedProject(null)
      userHasSelectedRef.current = false
    }
  }, [isOpen])
  
  // Handle pre-selection when projectData is loaded (only if selectedProjectCode is provided)
  useEffect(() => {
    if (isOpen && selectedProjectCode && projectData.length > 0 && !userHasSelectedRef.current) {
      const preSelected = projectData.find(item => 
        item.projectNumber === selectedProjectCode
      )
      if (preSelected) {
        setSelectedProject(preSelected)
      }
    }
  }, [projectData, isOpen, selectedProjectCode])

  // Handle ESC key to close drawer
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey)
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const loadData = async () => {
    setLoading(true)
    try {
      // Load only active projects
      const projectResponse = await projectService.getAllProjects({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: 'Active' // Only active projects
      })

      let projects = []
      if (projectResponse && projectResponse.success) {
        const projectsList = projectResponse.projects || projectResponse.data || []
        projects = (Array.isArray(projectsList) ? projectsList : []).map(project => ({
          ...project,
          _id: project._id || project.id,
          projectNumber: project.projectNumber || '',
          projectName: project.projectName || '',
          customer: project.customer || null,
          customerCode: project.customer?.customerCode || project.customerCode || '',
          customerName: project.customer?.customerName || project.customerName || ''
        }))
      }

      setProjectData(projects)
      
      // Calculate total pages and records
      const totalItems = projectResponse?.pagination?.totalItems || projects.length
      setTotalPages(Math.ceil(totalItems / itemsPerPage) || 1)
      setTotalRecords(totalItems)
      
    } catch (error) {
      console.error('Error loading projects:', error)
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        onClose()
        window.location.href = '/login'
        return
      }
      
      // Handle other errors
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access this data.')
      } else if (error.response?.status === 404) {
        toast.error('Service not found.')
      } else {
        toast.error('Failed to load projects. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setCurrentPage(1)
    loadData()
  }

  const handleProjectSelect = (item) => {
    if (!item) {
      return
    }
    
    userHasSelectedRef.current = true
    setSelectedProject(item)
  }

  const handleSaveAndClose = () => {
    if (selectedProject) {
      onSelectProject(selectedProject)
      onClose()
    } else {
      toast.error('Please select a project before proceeding')
      return
    }
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  if (!isOpen) return null

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div className={`project-drawer-overlay ${isOpen ? 'open' : ''}`} onClick={handleOverlayClick}>
      <div className={`project-drawer ${isOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="project-drawer-header">
          <div className="project-drawer-header-content">
            <h2>Project Master</h2>
            <p className="project-drawer-subtitle">
              Select a project from the list (Active projects only)
            </p>
          </div>
          <button className="project-drawer-close" onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </div>

        {/* Search and Pagination */}
        <div className="project-drawer-search-section">
          <form onSubmit={handleSearch} className="project-search-form">
            <div className="project-search-wrapper">
              <Search size={18} className="project-search-icon" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="project-search-input"
                placeholder="Search projects by name, code, or customer..."
              />
            </div>
          </form>
          
          <div className="project-pagination">
            <button
              className="project-pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="project-pagination-info">
              Page {currentPage} of {totalPages} ({totalRecords} total)
            </span>
            <button
              className="project-pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="project-drawer-content">
          {loading ? (
            <div className="project-loading">
              <div className="project-loading-spinner">Loading...</div>
            </div>
          ) : projectData.length === 0 ? (
            <div className="project-empty-state">
              <div className="empty-icon">📁</div>
              <h3>No records found</h3>
              <p>No active project records available.</p>
            </div>
          ) : (
            <div className="project-table-container">
              <table className="project-table">
                <thead>
                  <tr>
                    <th>Project Code</th>
                    <th>Project Name</th>
                    <th>Customer Code</th>
                    <th>Customer Name</th>
                    <th>Select</th>
                  </tr>
                </thead>
                <tbody>
                  {projectData.map((item) => {
                    const isSelected = selectedProject?._id === item._id || 
                      (selectedProject?.projectNumber === item.projectNumber)
                    return (
                      <tr 
                        key={item._id || item.id}
                        className={isSelected ? 'selected' : ''}
                        onClick={() => handleProjectSelect(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="project-code-cell">{item.projectNumber}</td>
                        <td className="project-name-cell">{item.projectName}</td>
                        <td className="project-customer-code-cell">{item.customerCode || 'N/A'}</td>
                        <td className="project-customer-name-cell">{item.customerName || 'N/A'}</td>
                        <td className="project-select-cell" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="radio"
                            name="selectedProject"
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleProjectSelect(item);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProjectSelect(item);
                            }}
                            className="project-radio"
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              
              {projectData.length === 0 && (
                <div className="project-no-data">
                  No active project records found.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="project-drawer-footer">
          <button className="project-btn-secondary" onClick={onClose}>
            <ChevronLeft size={18} />
            Cancel
          </button>
          <button 
            className="project-btn-primary"
            onClick={handleSaveAndClose}
          >
            Select Project
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProjectSelectionModal

